import { FC, Fragment, useMemo, useRef, useState } from 'react';

import { TLanguage } from '@lib/core/retailers/types';
import { useApp } from '@lib/core/service/hooks';
import { languages } from '@lib/tools/locale/utils/consts';
import useOutsideClick from '@lib/tools/views/hooks/useOutsideClick';

import GBicon from '@components/web/src/assets/icons/flags/GB.svg';
import ITicon from '@components/web/src/assets/icons/flags/IT.svg';
import { Flexbox } from '@components/web/src/foundations/Flexbox/Flexbox';
import LanguageItem from '@components/web/src/organisms/LanguageSwitcher/LanguageItem';
import LanguageList from '@components/web/src/organisms/LanguageSwitcher/LanguageList/LanguageList';
import * as S from '@components/web/src/organisms/LanguageSwitcher/styles';

export type ILanguageSwitcherType = 'select' | 'icon' | 'toggle';

interface IProps {
  languageSwitcherType: ILanguageSwitcherType;
  retailerLanguages: TLanguage[];
  locale: string;
}

export const LanguageSwitcher: FC<IProps> = ({ retailerLanguages, languageSwitcherType, locale }) => {
  const [showLanguagesList, setShowLanguagesList] = useState(false);
  const { handleSetServiceLocale } = useApp();

  const handleOutsideClick = () => setShowLanguagesList(false);
  const handleToggleLanguagesList = () => setShowLanguagesList(!showLanguagesList);

  const ref = useRef<HTMLDivElement>(null);
  useOutsideClick(showLanguagesList ? ref : null, handleOutsideClick);

  const handleSelectLanguage = selectedLanguageCode => {
    if (selectedLanguageCode !== locale) {
      handleSetServiceLocale(selectedLanguageCode);
    }
    setShowLanguagesList(false);
  };

  const currentLanguage = useMemo(() => retailerLanguages.find(({ code }) => code === locale), [locale]);
  const filteredLanguagesList = useMemo(() => retailerLanguages.filter(({ code }) => code !== locale), [locale]);

  if (!retailerLanguages || retailerLanguages?.length < 2) return null;
  return (
    <>
      {languageSwitcherType === 'select' && (
        <S.LanguageSwitcherContainer ref={ref}>
          <LanguageItem
            handleSelectLanguage={handleSelectLanguage}
            handleToggleLanguagesList={handleToggleLanguagesList}
            languageCode={currentLanguage?.code || locale}
            languageText={currentLanguage?.language || ''}
            showLanguagesList={showLanguagesList}
          />
          {showLanguagesList && (
            <LanguageList filteredLanguagesList={filteredLanguagesList} handleSelectLanguage={handleSelectLanguage} />
          )}
        </S.LanguageSwitcherContainer>
      )}
      {languageSwitcherType === 'icon' && (
        <S.IconContainer align="flex-start" gap={8} justify="flex-start">
          {retailerLanguages?.map(({ code }) => {
            const isSelected = locale === code;
            const icon = code === languages.ITALIAN ? ITicon : GBicon;

            return (
              <S.LanguageSwitcherButton key={code} $isSelected={isSelected} onClick={() => handleSelectLanguage(code)}>
                <S.IconImage $isSelected={isSelected} alt={`${code} icon`} src={icon} />
              </S.LanguageSwitcherButton>
            );
          })}
        </S.IconContainer>
      )}
      {languageSwitcherType === 'toggle' && (
        <Flexbox align="center" gap={7}>
          {retailerLanguages?.map(({ code }, index) => {
            const isSelected = locale === code;
            const isNotLast = index < retailerLanguages.length - 1;

            return (
              <Fragment key={code}>
                <S.LanguageSwitcherToggleButton onClick={() => handleSelectLanguage(code)}>
                  <S.ToggleText $isSelected={isSelected} size="subtitle2" text={code} textTransform="uppercase" />
                </S.LanguageSwitcherToggleButton>

                {isNotLast && <S.Divider />}
              </Fragment>
            );
          })}
        </Flexbox>
      )}
    </>
  );
};
